import * as React from 'react';
import { ErrorMessage } from '@/components/ui/error-message';
import { Callout, WorkspaceButton, useToast } from '@/components/ui/workspace';
import {
  DOCUMENT_PARTY_LABEL,
  useCanChangeDocumentParty,
  useChangeDocumentPartyMutation,
  type DocumentParty,
  type PartyHint as PartyHintValue,
} from '@/lib/api/documents';
import { cn } from '@/lib/utils/cn';
import { PartyChip } from './party-chip';
import { PartyChoice } from './party-choice';

/**
 * Changing whose document it is (E19 / T-0215, design §2 and §4).
 *
 * An engagement lead (or a firm admin) re-tags a document from the document
 * detail: "Change" opens the same "Whose document is this?" choice in
 * place, Save sends `PATCH /api/v1/documents/:id { source_party }`, and the
 * toast says the document is being re-read and that extraction must run
 * again to rebuild the baseline — with how many pending proposals the old
 * party had produced that were retired, when any were. Errors stay in place.
 *
 * The hint: when the classifier found Avero named as a party on page 1 of a
 * document tagged Vendor, the card and the detail say so, and a lead can tag
 * it as Avero in one click through the same PATCH. Everyone else sees the
 * note without the button; the route refuses them on its own.
 *
 * One direction: nothing here opens a modal. Tokens from the theme only.
 */

export const AVERO_HINT = 'Avero is named as a party on page 1 — tag as Avero?';

/** The toast after a change, in the ticket's words. */
export function partyChangedMessage(party: DocumentParty, rejectedProposalCount: number): string {
  const message = `Re-reading as ${DOCUMENT_PARTY_LABEL[party]}. Run extraction again to rebuild the baseline.`;
  if (rejectedProposalCount <= 0) return message;
  const noun = rejectedProposalCount === 1 ? 'proposal' : 'proposals';
  return `${message} ${rejectedProposalCount} ${noun} retired.`;
}

/** The hint applies: the document is tagged Vendor and page 1 names Avero. */
export function hasAveroHint(
  party: DocumentParty | null | undefined,
  partyHint: PartyHintValue | undefined,
): boolean {
  return party === 'vendor' && partyHint === 'avero';
}

/**
 * The change itself, with its toast. `change` sends the PATCH and, once the
 * server has answered, raises the toast from the server's counts.
 */
export function useChangeParty(engagementId: string) {
  const mutation = useChangeDocumentPartyMutation(engagementId);
  const { toast } = useToast();
  const { mutate } = mutation;
  const change = React.useCallback(
    (documentId: string, party: DocumentParty, onSuccess?: () => void) => {
      mutate(
        { documentId, party },
        {
          onSuccess: (result) => {
            toast({
              message: partyChangedMessage(
                result.source_party ?? party,
                result.rejected_proposal_count,
              ),
            });
            onSuccess?.();
          },
        },
      );
    },
    [mutate, toast],
  );
  return { change, isPending: mutation.isPending, error: mutation.error, reset: mutation.reset };
}

export interface PartyHintProps {
  engagementId: string;
  documentId: string;
  party: DocumentParty | null | undefined;
  partyHint: PartyHintValue | undefined;
  className?: string;
}

/**
 * "Avero is named as a party on page 1 — tag as Avero?" Nothing unless the
 * hint applies (`hasAveroHint`); a lead gets the one-click button.
 */
export function PartyHint(props: PartyHintProps) {
  if (!hasAveroHint(props.party, props.partyHint)) return null;
  return <AveroHint {...props} />;
}

function AveroHint({ engagementId, documentId, className }: PartyHintProps) {
  const canChange = useCanChangeDocumentParty();
  const { change, isPending, error } = useChangeParty(engagementId);
  return (
    <div data-testid="party-hint" className={cn('flex flex-col gap-2', className)}>
      <Callout
        tone="ask"
        actions={
          canChange ? (
            <WorkspaceButton
              variant="primary"
              size="sm"
              disabled={isPending}
              onClick={() => change(documentId, 'avero')}
            >
              {isPending ? 'Tagging…' : 'Tag as Avero'}
            </WorkspaceButton>
          ) : undefined
        }
      >
        {AVERO_HINT}
      </Callout>
      {error ? <ErrorMessage error={error} /> : null}
    </div>
  );
}

export interface PartyChangeFormProps {
  engagementId: string;
  documentId: string;
  /** The document's party now; the choice starts on it. */
  party: DocumentParty;
  /** Called after a save, and on Cancel. */
  onDone: () => void;
  className?: string;
}

/** The inline "Whose document is this?" choice with Save and Cancel. */
export function PartyChangeForm({
  engagementId,
  documentId,
  party,
  onDone,
  className,
}: PartyChangeFormProps) {
  const [draft, setDraft] = React.useState<DocumentParty>(party);
  const { change, isPending, error } = useChangeParty(engagementId);
  return (
    <form
      aria-label="Change whose document this is"
      className={cn(
        'flex flex-col gap-3 rounded-card border border-ink-100 bg-paper-50 p-3',
        className,
      )}
      onSubmit={(e) => {
        e.preventDefault();
        if (draft === party) return;
        change(documentId, draft, onDone);
      }}
    >
      <PartyChoice value={draft} onChange={setDraft} disabled={isPending} />
      {error ? <ErrorMessage error={error} /> : null}
      <div className="flex items-center gap-2">
        <WorkspaceButton
          type="submit"
          variant="primary"
          size="sm"
          disabled={isPending || draft === party}
        >
          {isPending ? 'Saving…' : 'Save'}
        </WorkspaceButton>
        <WorkspaceButton
          type="button"
          variant="secondary"
          size="sm"
          disabled={isPending}
          onClick={onDone}
        >
          Cancel
        </WorkspaceButton>
      </div>
    </form>
  );
}

export interface DocumentPartyControlProps {
  engagementId: string;
  documentId: string;
  party: DocumentParty;
  partyHint: PartyHintValue | undefined;
}

/**
 * The document detail's party: the chip, "Change" for leads with the inline
 * choice it opens, and the hint. Rendered inside a wrapping flex row; the
 * form and the hint take a full line of it.
 */
export function DocumentPartyControl({
  engagementId,
  documentId,
  party,
  partyHint,
}: DocumentPartyControlProps) {
  const canChange = useCanChangeDocumentParty();
  const [editing, setEditing] = React.useState(false);
  return (
    <>
      <PartyChip party={party} />
      {canChange && !editing ? (
        <WorkspaceButton
          variant="tertiary"
          size="sm"
          title="Change whose document this is"
          onClick={() => setEditing(true)}
        >
          Change
        </WorkspaceButton>
      ) : null}
      {editing ? (
        <div className="basis-full">
          <PartyChangeForm
            engagementId={engagementId}
            documentId={documentId}
            party={party}
            onDone={() => setEditing(false)}
          />
        </div>
      ) : null}
      {hasAveroHint(party, partyHint) ? (
        <div className="basis-full">
          <PartyHint
            engagementId={engagementId}
            documentId={documentId}
            party={party}
            partyHint={partyHint}
          />
        </div>
      ) : null}
    </>
  );
}
